/**
 * AssignmentFields — the study/negotiation/documentKind/direction/round/date
 * fields POST /intake/:id/approve and POST /intake/upload share (plan §4.4,
 * §13.4 WP-31 frontend scope: "study picker from listStudies or a new-study
 * form reusing the fields of tabs/studies/CreateStudyModal.tsx's input shape
 * without importing that component"). Used by both ApproveDialog and
 * UploadDialog so the two forms cannot drift apart.
 */
import { useEffect, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { fieldCls, FilterSelect, SegmentedControl } from '../../../ui';
import { listStudies, listNegotiations } from '../../api/client';
import type { Study, Negotiation, TagMatchCandidate, TagUnresolvedErrorBody } from '../../api/client';
import type { DocumentKind, IntakeAssignmentInput, IntakeNewStudyInput, NegotiationKind, VersionDirection } from '../../api/intake';
import { EMPTY_TAG_FIELD } from '../studies/TagField';
import type { TagFieldState } from '../studies/TagField';
import { MasterTagField } from './MasterTagField';
import type { IntakeTagListType } from './MasterTagField';

export interface NewStudyState {
  protocolNumber: string;
  status: string;
  title: string;
  sponsor: TagFieldState;
  cro: TagFieldState;
  therapeuticArea: TagFieldState;
  indication: TagFieldState;
}

export interface NewNegotiationState {
  kind: NegotiationKind;
  label: string;
}

export interface AssignmentState {
  studyMode: 'existing' | 'new';
  studyId: string;
  newStudy: NewStudyState;
  negotiationMode: 'existing' | 'new';
  negotiationId: string;
  newNegotiation: NewNegotiationState;
  documentKind: DocumentKind;
  direction: VersionDirection | '';
  round: string;
  versionDate: string;
}

export const EMPTY_ASSIGNMENT: AssignmentState = {
  studyMode: 'existing',
  studyId: '',
  newStudy: {
    protocolNumber: '',
    status: 'open',
    title: '',
    sponsor: EMPTY_TAG_FIELD,
    cro: EMPTY_TAG_FIELD,
    therapeuticArea: EMPTY_TAG_FIELD,
    indication: EMPTY_TAG_FIELD,
  },
  negotiationMode: 'existing',
  negotiationId: '',
  newNegotiation: { kind: 'budget', label: '' },
  documentKind: 'budget_workbook',
  direction: '',
  round: '',
  versionDate: '',
};

const DOCUMENT_KIND_OPTIONS: Array<{ value: DocumentKind; label: string }> = [
  { value: 'budget_workbook', label: 'Budget workbook' },
  { value: 'cta', label: 'CTA' },
  { value: 'budget_exhibit', label: 'Budget exhibit' },
  { value: 'protocol', label: 'Protocol' },
  { value: 'letter', label: 'Letter' },
  { value: 'email', label: 'Email' },
  { value: 'remittance', label: 'Remittance' },
  { value: 'other', label: 'Other' },
];

const DIRECTION_OPTIONS = [
  { value: '', label: 'Select direction…' },
  { value: 'received', label: 'Received' },
  { value: 'sent', label: 'Sent' },
  { value: 'internal', label: 'Internal' },
];

const NEGOTIATION_KIND_OPTIONS: Array<{ value: NegotiationKind; label: string }> = [
  { value: 'budget', label: 'Budget' },
  { value: 'contract', label: 'Contract' },
  { value: 'amendment_budget', label: 'Amendment (budget)' },
  { value: 'amendment_contract', label: 'Amendment (contract)' },
];

/** The four study tags, in the order CreateStudyModal shows them (plan §5.3, §7.5). The
 *  key type is taken from the 409 body so the form and the error can never disagree. */
export type NewStudyTagKey = TagUnresolvedErrorBody['field'];

const NEW_STUDY_TAG_DEFS: Array<{ key: NewStudyTagKey; label: string }> = [
  { key: 'sponsor', label: 'Sponsor' },
  { key: 'cro', label: 'CRO' },
  { key: 'therapeuticArea', label: 'Therapeutic area' },
  { key: 'indication', label: 'Indication' },
];

/** Pairs each tag key to its §5.2 master-list type (backend studies/service.js:40-45's own pairing). */
const TAG_LIST_TYPE: Record<NewStudyTagKey, IntakeTagListType> = {
  sponsor: 'sponsor',
  cro: 'cro',
  therapeuticArea: 'therapeutic_area',
  indication: 'indication',
};

/** What this tag will send: the picked entry id, else the typed text, trimmed. '' means "not set". */
function tagValue(field: TagFieldState): string {
  return field.resolvedId ?? field.text.trim();
}

/** Cannot submit until study, negotiation, direction, round and date are all set (plan
 *  §13.4 WP-31 frontend Verify line) and, for a new study, a sponsor (cta_studies.sponsor_id
 *  is NOT NULL). */
export function assignmentIsComplete(state: AssignmentState): boolean {
  const studyOk = state.studyMode === 'existing'
    ? state.studyId.trim().length > 0
    : state.newStudy.protocolNumber.trim().length > 0
      && state.newStudy.status.trim().length > 0
      && tagValue(state.newStudy.sponsor).length > 0;
  const negotiationOk = state.negotiationMode === 'existing'
    ? state.negotiationId.trim().length > 0
    : state.newNegotiation.kind.trim().length > 0;
  const roundOk = state.round.trim().length > 0 && Number.isInteger(Number(state.round));
  return studyOk && negotiationOk && state.direction.length > 0 && roundOk && state.versionDate.trim().length > 0;
}

function newStudyToInput(s: NewStudyState): IntakeNewStudyInput {
  const tagValues: Partial<Record<NewStudyTagKey, string>> = {};
  for (const { key } of NEW_STUDY_TAG_DEFS) {
    const value = tagValue(s[key]);
    if (value) tagValues[key] = value;
  }
  return {
    protocolNumber: s.protocolNumber.trim(),
    status: s.status,
    // ALWAYS sent, even empty: cta_studies.title is NOT NULL with no DEFAULT
    // (20260911040000_cta_core.sql:163) and createStudy only sets the column
    // when the key is present (studies/service.js:227).
    title: s.title.trim(),
    ...tagValues,
  };
}

export function assignmentToInput(state: AssignmentState): IntakeAssignmentInput {
  return {
    ...(state.studyMode === 'existing' ? { studyId: state.studyId } : { newStudy: newStudyToInput(state.newStudy) }),
    ...(state.negotiationMode === 'existing' ? { negotiationId: state.negotiationId } : { newNegotiation: state.newNegotiation }),
    documentKind: state.documentKind,
    direction: state.direction as VersionDirection,
    round: Number(state.round),
    versionDate: state.versionDate,
  };
}

/** Attach a 409's candidates to one tag so TagField offers them. Pure: the dialogs own
 *  the AssignmentState, so the 409 path writes through the same setState the form uses —
 *  a second write path into the same state is exactly how two forms drift apart. */
export function withTagCandidates(
  state: AssignmentState,
  field: NewStudyTagKey,
  candidates: TagMatchCandidate[],
): AssignmentState {
  const newStudy: NewStudyState = { ...state.newStudy };
  // Never leave [] on an Intake tag (blocking fix): TagField.tsx renders its
  // Admin-tab "add it from the Admin tab first" sentence whenever
  // `candidates` is truthy, even an empty array, and MasterTagField's own
  // "No close matches" line is suppressed the same way — an empty list from
  // the 409 must read the same as no list at all.
  newStudy[field] = { ...state.newStudy[field], candidates: candidates.length > 0 ? candidates : null };
  return { ...state, newStudy };
}

/** The one sentence both dialogs show for an unresolved tag (CreateStudyModal.tsx:124's wording). */
export function tagUnresolvedMessage(field: NewStudyTagKey): string {
  const label = NEW_STUDY_TAG_DEFS.find((f) => f.key === field)?.label ?? field;
  return `Could not resolve "${label}" — pick a match below, or keep typing.`;
}

interface Props {
  state: AssignmentState;
  onChange: Dispatch<SetStateAction<AssignmentState>>;
  idPrefix: string;
}

export function AssignmentFields({ state, onChange, idPrefix }: Props) {
  const [studies, setStudies] = useState<Study[] | null>(null);
  const [negotiations, setNegotiations] = useState<Negotiation[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    listStudies()
      .then((rows) => {
        if (!cancelled) setStudies(rows);
      })
      .catch(() => {
        if (!cancelled) setStudies([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (state.studyMode !== 'existing' || !state.studyId) {
      setNegotiations(null);
      return undefined;
    }
    let cancelled = false;
    listNegotiations(state.studyId)
      .then((rows) => {
        if (!cancelled) setNegotiations(rows);
      })
      .catch(() => {
        if (!cancelled) setNegotiations([]);
      });
    return () => {
      cancelled = true;
    };
  }, [state.studyMode, state.studyId]);

  function patch(partial: Partial<AssignmentState>) {
    onChange({ ...state, ...partial });
  }

  function patchTag(key: NewStudyTagKey, next: TagFieldState) {
    // Functional update (blocking fix): an async MasterTagField answer can
    // land after the person has edited something else in this form, and a
    // plain `onChange({ ...state, ...partial })` would spread the stale
    // `state` this closure captured at the moment they left the field,
    // wiping that later edit. Reading `prev` instead always applies onto
    // the current AssignmentState (PART J: "async onChange ... never the
    // render-time closure").
    onChange((prev) => {
      const newStudy: NewStudyState = { ...prev.newStudy };
      newStudy[key] = next;
      return { ...prev, newStudy };
    });
  }

  function setStudyMode(mode: 'existing' | 'new') {
    // Starting a new study means no existing negotiation can belong to it yet.
    patch(mode === 'new' ? { studyMode: mode, negotiationMode: 'new', negotiationId: '' } : { studyMode: mode });
  }

  return (
    <div className="space-y-4">
      <div>
        <div className="mb-1 flex items-center justify-between">
          <span className="block text-xs font-medium text-gray-600 dark:text-gray-300">Study</span>
          <SegmentedControl
            value={state.studyMode}
            onChange={(v) => setStudyMode(v as 'existing' | 'new')}
            options={[{ value: 'existing', label: 'Existing' }, { value: 'new', label: 'New study' }]}
          />
        </div>
        {state.studyMode === 'existing' ? (
          <FilterSelect
            label="Study"
            value={state.studyId}
            onChange={(studyId) => patch({ studyId })}
            options={[
              { value: '', label: studies === null ? 'Loading…' : 'Select a study…' },
              ...(studies ?? []).map((s) => ({ value: s.id, label: `${s.protocol_number}${s.title ? ` — ${s.title}` : ''}` })),
            ]}
          />
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <input
                id={`${idPrefix}-new-protocol`}
                type="text"
                placeholder="Protocol number"
                value={state.newStudy.protocolNumber}
                onChange={(e) => patch({ newStudy: { ...state.newStudy, protocolNumber: e.target.value } })}
                className={`${fieldCls} sm:col-span-2`}
              />
              <input
                id={`${idPrefix}-new-title`}
                type="text"
                placeholder="Title (optional)"
                value={state.newStudy.title}
                onChange={(e) => patch({ newStudy: { ...state.newStudy, title: e.target.value } })}
                className={fieldCls}
              />
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {NEW_STUDY_TAG_DEFS.map(({ key, label }) => (
                <MasterTagField
                  key={key}
                  id={`${idPrefix}-tag-${key}`}
                  label={label}
                  listType={TAG_LIST_TYPE[key]}
                  state={state.newStudy[key]}
                  onChange={(next) => patchTag(key, next)}
                />
              ))}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Protocol number and sponsor are required. Each tag must match a master-list entry — type it and pick from the matches offered, or add it as a new entry.
            </p>
          </div>
        )}
      </div>

      <div>
        <div className="mb-1 flex items-center justify-between">
          <span className="block text-xs font-medium text-gray-600 dark:text-gray-300">Negotiation</span>
          {state.studyMode === 'existing' && state.studyId && (
            <SegmentedControl
              value={state.negotiationMode}
              onChange={(v) => patch({ negotiationMode: v as 'existing' | 'new', negotiationId: '' })}
              options={[{ value: 'existing', label: 'Existing' }, { value: 'new', label: 'New negotiation' }]}
            />
          )}
        </div>
        {state.negotiationMode === 'existing' ? (
          <FilterSelect
            label="Negotiation"
            value={state.negotiationId}
            onChange={(negotiationId) => patch({ negotiationId })}
            disabled={!state.studyId}
            options={[
              { value: '', label: !state.studyId ? 'Choose a study first…' : negotiations === null ? 'Loading…' : 'Select a negotiation…' },
              ...(negotiations ?? []).map((n) => ({ value: n.id, label: `${n.kind}${n.label ? ` — ${n.label}` : ''}` })),
            ]}
          />
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FilterSelect
              label="Negotiation kind"
              value={state.newNegotiation.kind}
              onChange={(kind) => patch({ newNegotiation: { ...state.newNegotiation, kind: kind as NegotiationKind } })}
              options={NEGOTIATION_KIND_OPTIONS}
            />
            <input
              id={`${idPrefix}-new-negotiation-label`}
              type="text"
              placeholder="Label (optional)"
              value={state.newNegotiation.label}
              onChange={(e) => patch({ newNegotiation: { ...state.newNegotiation, label: e.target.value } })}
              className={fieldCls}
            />
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor={`${idPrefix}-document-kind`} className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
            Document kind
          </label>
          <FilterSelect
            label="Document kind"
            value={state.documentKind}
            onChange={(v) => patch({ documentKind: v as DocumentKind })}
            options={DOCUMENT_KIND_OPTIONS}
          />
        </div>
        <div>
          <label htmlFor={`${idPrefix}-direction`} className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
            Direction
          </label>
          <FilterSelect
            label="Direction"
            value={state.direction}
            onChange={(v) => patch({ direction: v as VersionDirection | '' })}
            options={DIRECTION_OPTIONS}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor={`${idPrefix}-round`} className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
            Round
          </label>
          <input
            id={`${idPrefix}-round`}
            type="number"
            min={0}
            step={1}
            value={state.round}
            onChange={(e) => patch({ round: e.target.value })}
            className={`${fieldCls} w-full`}
          />
        </div>
        <div>
          <label htmlFor={`${idPrefix}-version-date`} className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
            Version date
          </label>
          <input
            id={`${idPrefix}-version-date`}
            type="date"
            value={state.versionDate}
            onChange={(e) => patch({ versionDate: e.target.value })}
            className={`${fieldCls} w-full`}
          />
        </div>
      </div>
    </div>
  );
}

export default AssignmentFields;
